import { addRawDataset, insertDatasetBetween } from 'js/actions';
import PopUpForm from 'js/components/elements/pop-up-form/PopUpForm';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { ModalConfigureLLM } from 'new/elements/flows/ModalConfigureLLM';
import { Stack } from 'new/elements/Stack';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ActionMenu } from '../components/ActionMenu';
import { ActionItem } from '../components/ActionMenu/ActionMenu';
import { NodeBox } from '../components/NodeBox';
import { ResourceName } from '../components/ResourceName';
import classes from './ContextEnricher.module.scss';
import useAiContainers from 'js/components/pipelines/cards/hooks/useAiContainers';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import { ExpandableResult } from 'src/js/components/pipelines/cards/components/ExpandableResult';

interface ContextEnricherProps {
  data: any;
}

const enricherFields = {
  enricher_type: {
    name: 'enricher_type',
    display_name: '',
    type: 'string',
    inputType: 'select',
    options: [
      {
        label: 'Custom Enricher',
        value: 'custom',
        description: 'Create your own context enricher',
      },
    ],
  },
  enricher_name: {
    name: 'enricher_name',
    display_name: 'Name',
    placeholder: 'Name for your custom enricher',
    type: 'string',
    inputType: 'text',
    dependsOn: {
      name: 'enricher_type',
      value: 'custom',
    },
    default: '',
  },
  enricher_code: {
    name: 'enricher_code',
    display_name: 'Set code',
    type: 'string',
    inputType: 'code',
    fieldProps: {
      language: 'python',
      modalTitle: 'Custom Query Enricher',
    },
    default: 'custom_context_enricher = "Prompt goes here"',
    dependsOn: {
      name: 'enricher_type',
      value: 'custom',
    },
  },
};

export const ContextEnricher = (props: ContextEnricherProps) => {
  const { data = {} } = props;
  const { id, origin_node_id, code_container_id, children } = data;
  const dispatch = useAppDispatch();
  const { isEdit } = useDraftContext();

  const type = 'context_enricher';

  const { formData, fields, onFieldUpdated } = useAiContainers(data, enricherFields, type);

  const childDataSetId = useSelector(
    (state: any) => state.entities.flowNodes[children?.[0]]?.data_set_id,
  );
  const childSinkId = useSelector(
    (state: any) => state.entities.flowNodes[children?.[0]]?.data_set_id,
  );
  const queryResponse = useSelector(
    (state: any) =>
      (Object.values(state.rag.queryResponse[origin_node_id] || {})[0] as any)?.context_enricher,
  );

  const [isLLMModalOpen, setIsLLMModalOpen] = useState(false);

  const onAddChild = () => {
    // insert a new data set between this node and its child
    if (childDataSetId) {
      dispatch(
        insertDatasetBetween(id, childDataSetId, {
          custom_config: { node_type: 'context_enricher' },
        }),
      );
    } else if (childSinkId) {
      // create a data set with this node as parent_data_set_id and update the
      // sink to have parent_data_set_id: <created_data_set_id>
      return dispatch(
        addRawDataset({
          custom_config: { node_type: 'context_enricher' },
          parent_data_set_id: id,
        }),
      ).then(({ result }) => {
        // TODO: instead of updating the sink node with the new data set id,
        // use the new endpoint for inserting nodes
      });
    }
  };

  const onOpenLLMModal = () => {
    setIsLLMModalOpen(true);
  };

  return (
    <NodeBox data={data} className={classes.container}>
      <ResourceName {...props} hideActions>
        CONTEXT ENRICHER
      </ResourceName>
      {isEdit ? (
        <ActionMenu>
          <ActionItem onClick={onOpenLLMModal}>Add LLM Processor</ActionItem>
          {/* <ActionItem onClick={onAddChild}>Add Context Enricher</ActionItem> */}
        </ActionMenu>
      ) : null}
      <div className={classes.queryPromptContainer}>
        <Stack marginBlock="8px" gap="8px">
          <PopUpForm
            rootClassName={isEdit ? 'nodrag nowheel' : undefined}
            data={formData}
            fields={fields}
            onFieldUpdated={onFieldUpdated}
          />
          {queryResponse && (
            <>
              <NodeBox.Title>Sample Output</NodeBox.Title>
              <ExpandableResult value={queryResponse} />
            </>
          )}
        </Stack>
      </div>
      {isEdit ? (
        <ModalConfigureLLM dataSetId={id} open={isLLMModalOpen} setOpen={setIsLLMModalOpen} />
      ) : null}
    </NodeBox>
  );
};
